import {inject, Injectable} from '@angular/core';
import {
  LOG_LEVEL,
  Purchases
} from '@revenuecat/purchases-capacitor';
import {Capacitor} from '@capacitor/core';
import {
  CustomerInfo,
  PurchaseOptions,
  PurchaseProvider,
  PurchaseProviderConfig,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import {
  CapacitorSdkMapper
} from './capacitor-sdk-mapper';
import {
  CapacitorPurchasesAdapter
} from './capacitor-purchases-adapter';
import {
  CapacitorIosOfferResolver
} from './capacitor-ios-offer-resolver';
import {
  MetaAttributionService
} from '../meta-attribution/meta-attribution.service';
import {
  LoggerService
} from '../logging/logger.service';
import {
  ObservabilityLevel,
  ObservabilityService
} from '../observability/observability.service';

/**
 * Wraps the RevenueCat Capacitor SDK behind the
 * PurchaseProvider interface. Uses CapacitorSdkMapper
 * to convert SDK types to our interface types.
 */
@Injectable()
export class CapacitorPurchaseProvider
  implements PurchaseProvider {

  // RevenueCat publishable mobile API keys —
  // safe to include in client-side code
  private static readonly API_KEYS:
    Record<string, string> = {
      ios: 'appl_GJvVtzPCshLxjUlFJdCIRuZCMRI',
      android: 'goog_iRMBhhIJLJVcbWUMIVzkXQMATYJ'
    };

  // The prefix the RevenueCat SDK uses for the anonymous id it
  // generates on first launch, before any logIn() succeeds.
  private static readonly RC_ANONYMOUS_ID_PREFIX =
    '$RCAnonymousID:';

  private readonly metaAttribution =
    inject(MetaAttributionService);
  private readonly logger = inject(LoggerService);
  private readonly observability =
    inject(ObservabilityService);
  private readonly mapper =
    new CapacitorSdkMapper();
  private readonly adapter =
    inject(CapacitorPurchasesAdapter);
  private readonly iosOfferResolver =
    inject(CapacitorIosOfferResolver);

  /** Cache of native SDK products keyed by id */
  private readonly sdkProductCache =
    new Map<string, any>();

  private getApiKey(): string {
    const platform = Capacitor.getPlatform();
    const key =
      CapacitorPurchaseProvider.API_KEYS[platform];
    if (!key) {
      throw new Error(
        'Unsupported platform for Purchases: '
        + platform
      );
    }
    return key;
  }

  private static isAnonymous(id: string): boolean {
    return id.startsWith(
      CapacitorPurchaseProvider.RC_ANONYMOUS_ID_PREFIX
    );
  }

  /**
   * Reads the SDK's current app user id and records it to Sentry so
   * an identity-merge failure (the HEC-338 incident: logIn() resolved
   * but the SDK stayed anonymous) leaves evidence next time. An id
   * that is anonymous, or that is not the account sub the call site
   * expected, is reported at warning level unless `beforeLogIn` says
   * no logIn as that account has been attempted yet, so whatever the
   * SDK holds is expected. Diagnostics only: a failed read returns
   * undefined and a failed report is logged, so neither can fail the
   * configure/logIn/purchase call it was reporting on — and a failed
   * report never hides an id that was read successfully.
   */
  private async reportIdentity(
    source: string,
    expectedAppUserId?: string,
    extra?: {
      created?: boolean;
      originalAppUserId?: string;
      beforeLogIn?: boolean;
    }
  ): Promise<string | undefined> {
    let appUserID: string;
    try {
      ({ appUserID } = await this.adapter.getAppUserID());
    } catch (error) {
      this.logger.warn(
        'CapacitorPurchaseProvider: failed to read the SDK '
        + `identity for source "${source}"`,
        error
      );
      return undefined;
    }
    try {
      const anonymous =
        CapacitorPurchaseProvider.isAnonymous(appUserID);
      const asExpected = expectedAppUserId === undefined
        || appUserID === expectedAppUserId;
      const level =
        (anonymous || !asExpected) && !extra?.beforeLogIn
          ? ObservabilityLevel.WARNING
          : ObservabilityLevel.INFO;
      this.observability.trackSubscriptionIdentity(
        appUserID, anonymous, level,
        {
          source,
          expectedAppUserId,
          created: extra?.created,
          originalAppUserId: extra?.originalAppUserId,
          platform: Capacitor.getPlatform()
        }
      );
    } catch (error) {
      this.logger.warn(
        'CapacitorPurchaseProvider: failed to report the SDK '
        + `identity for source "${source}"`,
        error
      );
    }
    return appUserID;
  }

  async configure(
    config: PurchaseProviderConfig
  ): Promise<void> {
    await this.adapter.configure({
      apiKey: this.getApiKey(),
      ...(config.appUserID
        ? { appUserID: config.appUserID }
        : {})
    });
    if (config.appUserID) {
      await this.reportIdentity(
        'configure', config.appUserID
      );
    } else {
      // The landing page configures anonymously by design —
      // this is the expected start of the chain the incident
      // followed, so it is reported at info level only.
      await this.reportIdentity(
        'configure_anonymous', undefined,
        { beforeLogIn: true }
      );
    }
  }

  async isConfigured(): Promise<boolean> {
    const status =
      await Purchases.isConfigured();
    return status?.isConfigured ?? false;
  }

  async logIn(appUserID: string): Promise<void> {
    // Anonymous, or a previous account, is normal here — this call
    // is what is meant to fix it — so record the arrived-in id
    // without a warning.
    await this.reportIdentity(
      'pre_login', appUserID, { beforeLogIn: true }
    );
    const result = await this.adapter.logIn({ appUserID });
    await this.reportIdentity(
      'login', appUserID,
      {
        created: result.created,
        originalAppUserId:
          result.customerInfo.originalAppUserId
      }
    );
  }

  async setLogLevel(
    level: string
  ): Promise<void> {
    const levelMap: Record<string, LOG_LEVEL> = {
      debug: LOG_LEVEL.DEBUG,
      info: LOG_LEVEL.INFO,
      warn: LOG_LEVEL.WARN,
      error: LOG_LEVEL.ERROR
    };
    const sdkLevel =
      levelMap[level] ?? LOG_LEVEL.ERROR;
    await Purchases.setLogLevel({
      level: sdkLevel
    });
  }

  async getOfferings():
    Promise<SubscriptionPackage[]> {
    const offerings =
      await this.adapter.getOfferings();
    const packages =
      offerings.current?.availablePackages ?? [];
    const mapped = packages.map(p => {
      const pkg = this.mapper.mapPackage(p);
      this.sdkProductCache.set(
        pkg.product.identifier, p.product
      );
      return pkg;
    });
    // The whole iOS discounted-offer story is resolved
    // here, invisibly to callers. Android/web keep the
    // shape-derived default from the mapper.
    if (Capacitor.getPlatform() === 'ios') {
      await this.iosOfferResolver.resolve(
        mapped, this.sdkProductCache
      );
    }
    return mapped;
  }

  async purchase(
    product: SubscriptionProduct,
    options?: PurchaseOptions
  ): Promise<{ customerInfo: CustomerInfo }> {
    const sdkProduct =
      this.sdkProductCache.get(product.identifier);
    if (!sdkProduct) {
      throw new Error(
        'Product not found in cache. '
        + 'Call getOfferings() before purchase().'
      );
    }

    await this.ensureIdentified(options?.appUserID);

    // Route on the DTO the user actually saw — never on
    // the signature cache, which is a signature store only.
    const result = product.effectiveOffer?.kind
      === 'promotional'
      ? await this.purchasePromotionalProduct(
        product, sdkProduct
      )
      : await this.adapter.purchaseStoreProduct({
        product: sdkProduct
      });

    return {
      customerInfo:
        this.mapper.mapCustomerInfo(
          result.customerInfo
        )
    };
  }

  /**
   * Self-heals the SDK identity immediately before a store purchase —
   * the HEC-338 fix. If the SDK does not hold the account sub the
   * purchase must be attributed to — still anonymous, or still a
   * previous account from the same device — and we have that sub,
   * logs in before letting the purchase proceed, so the webhook's
   * `aliases` carry the right account UUID instead of arriving
   * unattributed or attributed to someone else. Both purchase routes
   * are login-guarded upstream, so an absent `sub` here shouldn't
   * arise in practice — the warning-level record from
   * reportIdentity() is the backstop if it ever does.
   */
  private async ensureIdentified(sub?: string): Promise<void> {
    const id = await this.reportIdentity('pre_purchase', sub);
    if (id === undefined || !sub || id === sub) {
      return;
    }

    const result = await this.adapter.logIn({ appUserID: sub });
    const after = await this.reportIdentity(
      'pre_purchase_login', sub,
      {
        created: result.created,
        originalAppUserId:
          result.customerInfo.originalAppUserId
      }
    );
    if (after !== undefined && after !== sub) {
      // Never word this with "cancelled": buy() reads that substring
      // as a user cancellation and would swallow the error silently.
      throw new Error(
        'RevenueCat identity is not the account sub after logIn, '
        + 'purchase aborted'
      );
    }
  }

  /**
   * Re-signs the promotional offer immediately before purchase (signed
   * offers can expire). If re-signing fails, falls back to the
   * load-time signature cached by CapacitorIosOfferResolver. If there
   * is no signature at all, or the store rejects the one we have, this
   * throws — the purchase FAILS through the caller's existing
   * trackPurchaseFailed/errorService path. It must never silently
   * degrade to a plain purchase, which would charge the standard price
   * against a product the user was shown at a promotional price.
   */
  private async purchasePromotionalProduct(
    product: SubscriptionProduct,
    sdkProduct: any
  ) {
    const discount = sdkProduct.discounts?.[0];
    let offer = discount
      ? await this.adapter.getPromotionalOffer({
        product: sdkProduct, discount
      }).catch(error => {
        this.logger.warn(
          'Promotional offer re-sign failed at '
          + `purchase time for ${product.identifier}`
          + ', falling back to the load-time '
          + 'signature if available', error
        );
        this.observability
          .trackPromotionalOfferSigningFailed(
            product.identifier,
            error instanceof Error
              ? error.message : 'Unknown error',
            { source: 'purchase_time_resign' }
          );
        return undefined;
      })
      : undefined;

    if (!offer) {
      offer = this.iosOfferResolver
        .getCachedOffer(product.identifier);
    }
    if (!offer) {
      throw new Error(
        'No signed promotional offer available for '
        + product.identifier
      );
    }

    return this.adapter.purchaseDiscountedProduct({
      product: sdkProduct, discount: offer
    });
  }

  async addCustomerInfoUpdateListener(
    listener: (info: CustomerInfo) => void
  ): Promise<void> {
    await Purchases.addCustomerInfoUpdateListener(
      (info) => {
        listener(
          this.mapper.mapCustomerInfo(info)
        );
      }
    );
  }

  async restorePurchases(): Promise<{
    customerInfo: CustomerInfo;
  }> {
    const result =
      await Purchases.restorePurchases();
    return {
      customerInfo:
        this.mapper.mapCustomerInfo(
          result.customerInfo
        )
    };
  }

  // --- Code redemption ---

  isRedeemCodeAvailable(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  }

  async redeemCode(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      await Purchases.presentCodeRedemptionSheet();
      return;
    }
    if (platform === 'android') {
      // No programmatic redemption on Google Play.
      // The component shows an explanation dialog.
      return;
    }
    throw new Error(
      'Code redemption not supported on platform: '
      + platform
    );
  }

  // --- Management ---

  isManagementAvailable(): boolean {
    return false;
  }

  async openManagementPage(): Promise<void> {
    /* no-op — mobile uses native store */
  }

  // --- Attribution ---

  async initialiseAttribution(): Promise<void> {
    try {
      await this.metaAttribution
        .enableAutoLogEvents();
      await Purchases.collectDeviceIdentifiers();

      const identifiers =
        await this.metaAttribution
          .getAttributionIdentifiers();
      if (identifiers.fbAnonymousId) {
        await Purchases.setFBAnonymousID({
          fbAnonymousID:
            identifiers.fbAnonymousId
        });
      }
    } catch (error) {
      this.logger.warn(
        'CapacitorPurchaseProvider: '
        + 'Attribution failed',
        error
      );
    }
  }

  async setAttributes(
    attributes: Record<string, string | null>
  ): Promise<void> {
    await Purchases.setAttributes(attributes);
  }

  async setFBAnonymousID(
    fbAnonymousId: string
  ): Promise<void> {
    await Purchases.setFBAnonymousID({
      fbAnonymousID: fbAnonymousId
    });
  }

  async collectDeviceIdentifiers():
    Promise<void> {
    await Purchases.collectDeviceIdentifiers();
  }
}
